import { default as Enumerable } from "linq";
import {
  ExtendedRouteConfig,
  AbstractRoutesFactory,
  RoutesFactory as BaseRoutesFactory,
} from "../../base/routing";
import {
  Home,
  ErrorView,
} from "../components";
import {
  FormatListView,
  EditHistoryView,
  NewFormatEditView,
  FormatEditView,
  NewCopiedFormatEditView,
  FormatView,
  UnsynchronizedFormatListView,
} from "../components/format";
import {
  PdfFileView,
  EmbeddedEditableFormatSummariesTreeSelectorView,
} from "../../documaker/components/document";
import {
  NewDocumentView,
} from "../components/document";
import { Dispatcher } from "./Dispatcher";
import { Query } from "./Query";
import {
  RegisterNewAccountEntryView,
  RegisterNewAccountView,
  ResetAccountPasswordEntryView,
  ResetAccountPasswordView,
} from "../../../components/account";

export class RoutesFactory extends AbstractRoutesFactory<Query, Dispatcher<Query>> {
  constructor(dispatcher: Dispatcher<Query>) {
    super(dispatcher);
  }

  create(): ExtendedRouteConfig[] {
    const baseFactory = new BaseRoutesFactory(this._dispatcher);
    return Enumerable.from(baseFactory.create())
      .select(r => this.hook(r))
      .toArray()
      .concat([
        {
          path: "/",
          name: "メインメニュー",
          component: Home,
          meta: { requiresAuth: true }
        },
        {
          path: "/ShowFormatList",
          name: "フォーマット一覧",
          component: FormatListView,
          meta: { requiresAuth: true }
        },
        {
          path: "/ShowEditHistory",
          name: "編集履歴",
          component: EditHistoryView,
          meta: { requiresAuth: true }
        },
        {
          path: "/EditNewFormat",
          name: "フォーマット新規作成",
          component: NewFormatEditView,
          meta: { requiresAuth: true },
        },
        {
          path: "/EditFormat",
          name: "フォーマット編集",
          component: FormatEditView,
          meta: { requiresAuth: true },
          props: r => this.toProps(r, q => ({
            formatId: q.formatId,
          })),
          beforeEnter: (to, from, next) => this._dispatcher.dispatchOnEditFormat(to, next),
        },
        {
          path: "/ShowUnsynchronizedFormatList",
          name: "未同期フォーマットリスト",
          component: UnsynchronizedFormatListView,
          meta: { requiresAuth: true },
        },
        {
          path: "/EditNewCopiedFormat",
          name: "フォーマットコピー",
          component: NewCopiedFormatEditView,
          meta: { requiresAuth: true },
          props: r => this.toProps(r, q => ({
            sourceFormatId: q.formatId,
            sourceArchiveId: q.archiveId,
          })),
          beforeEnter: (to, from, next) => this._dispatcher.dispatchOnAccessFormat(to, next),
        },
        {
          path: "/PreviewNewDocument",
          name: "文書編集プレビュー",
          component: NewDocumentView,
          meta: { requiresAuth: true },
          props: r => this.toProps(r, q => ({
            patientNo: q.patientNo,
            formatId: q.formatId,
            archiveId: q.archiveId,
          })),
          beforeEnter: (to, from, next) => this._dispatcher.dispatchOnAccessFormat(to, next),
        },
        {
          path: "/PreviewNewTemporaryDocument",
          name: "編集中文書プレビュー",
          component: NewDocumentView,
          meta: { requiresAuth: true },
          props: r => this.toProps(r, q => ({
            patientNo: q.patientNo,
            formatId: q.formatId,
            archiveId: q.archiveId,
          })),
          beforeEnter: (to, from, next) => this._dispatcher.dispatchOnAccessFormat(to, next),
        },
        {
          path: "/ShowFormat",
          name: "フォーマット参照",
          meta: { requiresAuth: true },
          component: FormatView,
          props: r => this.toProps(r, q => ({
            formatId: q.formatId,
            archiveId: q.archiveId,
            shouldNotifyRegistered: q.shouldNotifyRegistered,
            isTemporary: q.isTemporary,
          })),
          beforeEnter: (to, from, next) => this._dispatcher.dispatchOnAccessFormat(to, next),
        },
        {
          path: "/ShowDocumentSimply",
          name: "文書表示",
          component: PdfFileView,
          meta: { requiresAuth: true },
          props: r => this.toProps(r, q => ({
            label: q.label,
            name: q.name,
            encodedUri: q.encodedUri,
            isPrintable: q.isPrintable,
            forceEnableOpenTypeFontFace: q.forceEnableOpenTypeFontFace,
          }))
        },
        {
          path: "/Embedded/SelectFolder",
          name: "フォルダ選択(埋込)",
          component: EmbeddedEditableFormatSummariesTreeSelectorView,
          meta: { requiresAuth: true },
        },
        {
          path: "/RegisterNewAccountEntry",
          name: "新規ユーザー登録メール送信",
          component: RegisterNewAccountEntryView,
          meta: { requiresAuth: false },
          props: r => this.toProps(r, q => ({
          }))
        },
        {
          path: "/RegisterNewAccount",
          name: "新規ユーザー登録",
          component: RegisterNewAccountView,
          meta: { requiresAuth: false },
          props: r => this.toProps(r, q => ({
            entryGuid: q.entryGuid,
          }))
        },
        {
          path: "/ResetAccountPasswordEntry",
          name: "パスワード再設定メール送信",
          component: ResetAccountPasswordEntryView,
          meta: { requiresAuth: false },
          props: r => this.toProps(r, q => ({
          }))
        },
        {
          path: "/ResetAccountPassword",
          name: "パスワード再設定",
          component: ResetAccountPasswordView,
          meta: { requiresAuth: false },
          props: r => this.toProps(r, q => ({
            entryGuid: q.entryGuid,
          }))
        },
      ]);
  }

  private hook(route: ExtendedRouteConfig): ExtendedRouteConfig {
    if (route.path === "/Error") {
      return   {
        path: "/Error",
        name: "エラー",
        component: ErrorView,
        props: r => this.toProps(r, q => ({
          url: q.url,
          message: q.message,
        }))
      };
    }
    return route;
  }
}
